import { makeAutoObservable, runInAction } from 'mobx'

import { LoadOwnerActivitiesTask } from '../Tasks/LoadOwnerActivitiesTask'
import { LoadOwnerActivityCountersTask } from '../Tasks/LoadOwnerActivityCountersTask'

import type { ActivityOwnerKind, ActivityRecord } from '../Contracts/Activity.types'

/**
 * Справи одного власника у вкладці його картки (`SPEC-011` §8.6): сторінка
 * списку, рядок статусів «Всі · Нова · Завершена» з лічильниками.
 *
 * Стан — тут, а не в ядровому списку: вкладений `ResourceList` чужого ресурсу
 * в картці підхоплює її контекст, і збереження справи пішло б на адресу
 * власника (`CORE-ISSUES` §3, Р2). Тому вкладка — власна таблиця, а картку
 * справи відкриває `OpenResourceCardTask` поза контекстом власника.
 */
export class OwnerActivitiesStore {
    items: ActivityRecord[] = []
    total = 0
    page = 1
    /** Код статусу; `undefined` — «Всі» */
    status: string | undefined = undefined
    /** Лічильники плашок; `null` — ще не прийшли (без числа, а не нуль) */
    counts: Record<string, number> | null = null
    countsTotal: number | null = null
    isLoading = false
    failed = false

    private ownerUuid: string | null = null
    private requestId = 0

    constructor(
        private readonly owner: ActivityOwnerKind,
        readonly pageSize: number,
        private readonly withCounters: boolean
    ) {
        makeAutoObservable<OwnerActivitiesStore, 'owner' | 'ownerUuid' | 'requestId' | 'withCounters'>(
            this,
            { owner: false, ownerUuid: false, requestId: false, withCounters: false, pageSize: false },
            { autoBind: true }
        )
    }

    /** Відкрили вкладку іншого власника — з першої сторінки й «Всіх». */
    bind(ownerUuid: string | undefined): void {
        if (!ownerUuid || ownerUuid === this.ownerUuid) return

        this.ownerUuid = ownerUuid
        this.page = 1
        this.status = undefined
        void this.reload()
    }

    setPage(page: number): void {
        this.page = page
        void this.load()
    }

    setStatus(status: string | undefined): void {
        if (status === this.status) return

        this.status = status
        this.page = 1
        void this.load()
    }

    /** Список і лічильники — після створення, завершення, закриття картки. */
    async reload(): Promise<void> {
        await Promise.all([this.load(), this.withCounters ? this.loadCounters() : Promise.resolve()])
    }

    private async load(): Promise<void> {
        const ownerUuid = this.ownerUuid

        if (!ownerUuid) return

        const requestId = ++this.requestId

        this.isLoading = true
        this.failed = false

        try {
            const { items, total } = await LoadOwnerActivitiesTask({
                owner: this.owner,
                ownerUuid,
                status: this.status,
                page: this.page,
                pageSize: this.pageSize,
            })

            // Відповідь на застарілий запит (клікнули іншу плашку) — не наша
            if (requestId !== this.requestId) return

            runInAction(() => {
                this.items = items
                this.total = total
            })
        } catch {
            if (requestId !== this.requestId) return

            runInAction(() => {
                this.items = []
                this.total = 0
                this.failed = true
            })
        } finally {
            if (requestId === this.requestId) {
                runInAction(() => {
                    this.isLoading = false
                })
            }
        }
    }

    private async loadCounters(): Promise<void> {
        const ownerUuid = this.ownerUuid

        if (!ownerUuid) return

        try {
            const response = await LoadOwnerActivityCountersTask(this.owner, ownerUuid)

            runInAction(() => {
                this.countsTotal = Number(response.total ?? 0)
                // Статуси без записів бек не віддає — для них плашка покаже нуль
                this.counts = { ...(response.byStatus ?? {}) }
            })
        } catch {
            // Плашки — підказка над таблицею: без цифр вони лишаються робочими
        }
    }
}
